import { useMemo, useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Header } from "@/components/Header";
import { InputPanel } from "@/components/InputPanel";
import { HeroMetrics } from "@/components/HeroMetrics";
import { IncomePaycheck } from "@/components/IncomePaycheck";
import { IncomeVsExpenses } from "@/components/IncomeVsExpenses";
import { ProjectionChart } from "@/components/ProjectionChart";
import { LongevityGauge } from "@/components/LongevityGauge";
import { AllocationDonut } from "@/components/AllocationDonut";
import { ScenarioCompare } from "@/components/ScenarioCompare";
import { AIAdvisor } from "@/components/AIAdvisor";
import { ExportBar } from "@/components/ExportBar";
import { ScheduleTab } from "@/components/ScheduleTab";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { buildDefaultInputs } from "@/lib/constants";
import { computePlan, buildSchedule } from "@/lib/calc";
import { fetchSharedPlan, createShareLink, copyToClipboard } from "@/lib/share";
import { toast } from "sonner";

export default function Planner() {
  const [inputs, setInputs] = useState(() => buildDefaultInputs("INR"));
  const [scenarios, setScenarios] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("aura_scenarios")) || [];
    } catch {
      return [];
    }
  });

  const result = useMemo(() => computePlan(inputs), [inputs]);
  const schedule = useMemo(() => buildSchedule(inputs, result), [inputs, result]);

  useEffect(() => {
    localStorage.setItem("aura_scenarios", JSON.stringify(scenarios));
  }, [scenarios]);

  // Load a shared plan if opened via ?plan=<id>
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const planId = params.get("plan");
    if (!planId) return;
    fetchSharedPlan(planId)
      .then((loaded) => {
        if (loaded) {
          setInputs({ ...buildDefaultInputs(loaded.currency || "INR"), ...loaded });
          toast.success("Shared plan loaded", { description: "You're viewing someone's shared retirement plan." });
        }
      })
      .catch(() => toast.error("That shared plan link is invalid or expired."))
      .finally(() => {
        window.history.replaceState({}, "", window.location.pathname);
      });
  }, []);

  const shareScenario = async (sc) => {
    try {
      const url = await createShareLink(sc.inputs, sc.label);
      const copied = await copyToClipboard(url);
      toast.success(copied ? `${sc.label} link copied!` : `${sc.label} link created`, {
        description: url,
        duration: copied ? 4000 : 10000,
      });
    } catch {
      toast.error("Could not create share link.");
    }
  };

  const saveScenario = () => {
    setScenarios((prev) => {
      if (prev.length >= 3) {
        toast.error("You can compare up to 3 scenarios. Remove one first.");
        return prev;
      }
      const label = `Scenario ${String.fromCharCode(65 + prev.length)}`;
      toast.success(`${label} saved`);
      return [
        ...prev,
        {
          id: Date.now(),
          label,
          inputs: JSON.parse(JSON.stringify(inputs)),
          result: JSON.parse(JSON.stringify(result)),
        },
      ];
    });
  };
  const removeScenario = (id) => setScenarios((prev) => prev.filter((s) => s.id !== id));
  const clearScenarios = () => setScenarios([]);

  const handleCurrency = (code) => {
    setInputs((p) => ({ ...p, currency: code }));
  };

  return (
    <div className="min-h-screen aura-grain">
      <Header currency={inputs.currency} onCurrencyChange={handleCurrency} />

      {/* Hero */}
      <div className="relative overflow-hidden border-b border-[#E8E3D9]">
        <div className="absolute inset-0">
          <img
            src="https://static.prod-images.emergentagent.com/jobs/afc10d1d-fd46-4f4c-932c-d61736f98c3e/images/849f84a5ccf6a9f1c02fc8a947fd2a82ecf3d6b747b2fef6ea32f6d26af3c09e.jpeg"
            alt="Mountain sunset"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1E2B25]/92 via-[#1E2B25]/70 to-[#1E2B25]/30" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-14 sm:py-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-2xl"
          >
            <span className="inline-block text-xs uppercase tracking-[0.2em] font-semibold text-[#D99B26] mb-3">
              Plan with confidence
            </span>
            <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white leading-tight">
              Design a retirement that <span className="text-[#D99B26]">outlasts you</span>.
            </h1>
            <p className="mt-4 text-base sm:text-lg text-white/75 leading-relaxed">
              Bring together your SWP, rental, FD, PPF and pension into one clear monthly paycheck.
              Choose a simple SWP or a 3/4-bucket strategy, let the corpus fund only the gap, stress-test
              it and compare scenarios — all with inflation, city cost of living and longevity built in.
            </p>
          </motion.div>
        </div>
      </div>

      {/* Dashboard */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 p-4 sm:p-6 lg:p-10 max-w-7xl mx-auto">
        <div className="lg:col-span-5">
          <InputPanel inputs={inputs} setInputs={setInputs} result={result} />
        </div>

        <div className="lg:col-span-7" data-testid="results-viewport">
          <Tabs defaultValue="dashboard" className="w-full">
            <TabsList className="bg-[#F5F2EB] p-1 rounded-xl mb-6 h-auto">
              <TabsTrigger value="dashboard" data-testid="tab-dashboard"
                className="rounded-lg px-4 py-2 text-sm font-semibold data-[state=active]:bg-[#2D6A4F] data-[state=active]:text-white">
                Dashboard
              </TabsTrigger>
              <TabsTrigger value="schedule" data-testid="tab-schedule"
                className="rounded-lg px-4 py-2 text-sm font-semibold data-[state=active]:bg-[#2D6A4F] data-[state=active]:text-white">
                Detailed Schedule
              </TabsTrigger>
            </TabsList>

            <TabsContent value="dashboard" className="space-y-6 mt-0">
              <IncomePaycheck result={result} currency={inputs.currency} />
              <HeroMetrics result={result} currency={inputs.currency} planEndAge={inputs.longevityAge} />
              <IncomeVsExpenses result={result} currency={inputs.currency} />
              <ProjectionChart result={result} currency={inputs.currency} />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <LongevityGauge result={result} />
                <AllocationDonut inputs={inputs} result={result} currency={inputs.currency} />
              </div>
              <AIAdvisor inputs={inputs} result={result} />
              <ScenarioCompare
                scenarios={scenarios}
                currency={inputs.currency}
                onSave={saveScenario}
                onRemove={removeScenario}
                onClear={clearScenarios}
                onShare={shareScenario}
                canSave={scenarios.length < 3}
              />
              <ExportBar inputs={inputs} result={result} schedule={schedule} />
            </TabsContent>

            <TabsContent value="schedule" className="mt-0">
              <ScheduleTab schedule={schedule} currency={inputs.currency} />
            </TabsContent>
          </Tabs>
        </div>
      </div>

      <footer className="border-t border-[#E8E3D9] py-6 text-center">
        <p className="text-xs text-[#8A9790]">
          My Finanz Planner · Estimates are illustrative and not financial advice. Assumptions drive results — adjust freely.
        </p>
      </footer>
    </div>
  );
}
